import React, { useMemo, useState, useCallback } from 'react';
import {
  RISK_SCORE_CHART_MAX,
  clampRiskScoreForChart,
  riskScoreToChartY,
  warnIfRiskChartMappingDrift,
} from '../../lib/riskChartScale';

const Y_TICKS = [0, 25, 50, 75, 100];

/**
 * Employee risk history line chart — 1:1 mapping of riskScore to y-axis (0–100).
 * Uses raw history riskScore values only; no normalization or auto-scaling.
 */
export default function RiskScoreHistoryChart({
  points = [],
  height = 200,
  className = '',
  currentScore = null,
}) {
  const [hover, setHover] = useState(null);

  const chart = useMemo(() => {
    const raw = (Array.isArray(points) ? points : [])
      .map((p) => ({
        score: clampRiskScoreForChart(p?.score ?? p?.riskScore),
        recordedAt: p?.recordedAt ?? p?.date ?? null,
        label: p?.label ?? null,
      }))
      .filter((p) => p.score != null);

    if (raw.length < 2) return null;

    const pad = { top: 16, bottom: 28, left: 36, right: 12 };
    const w = 480;
    const h = height;
    const innerW = w - pad.left - pad.right;
    const innerH = h - pad.top - pad.bottom;
    const layout = { innerH, padTop: pad.top };

    const n = raw.length;
    const toX = (i) => pad.left + (i / Math.max(1, n - 1)) * innerW;
    const pts = raw.map((row, i) => {
      const y = riskScoreToChartY(row.score, layout);
      return {
        x: toX(i),
        y,
        score: row.score,
        recordedAt: row.recordedAt,
        label: row.label,
        i,
      };
    });

    warnIfRiskChartMappingDrift(
      pts.map((p) => ({ score: p.score, y: p.y, layout })),
      'RiskScoreHistoryChart'
    );

    const linePath = pts
      .map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
      .join(' ');

    const gridLines = Y_TICKS.map((tick) => ({
      y: riskScoreToChartY(tick, layout),
      value: tick,
    }));

    const latest = pts[pts.length - 1];
    const current = clampRiskScoreForChart(currentScore);

    return { w, h, pad, pts, linePath, gridLines, innerW, latest, current };
  }, [points, height, currentScore]);

  const onMove = useCallback(
    (e) => {
      if (!chart) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * chart.w;
      const ratio = Math.max(0, Math.min(1, (x - chart.pad.left) / chart.innerW));
      const idx = Math.round(ratio * (chart.pts.length - 1));
      const p = chart.pts[idx];
      if (!p) return;
      setHover({ ...p, x: p.x });
    },
    [chart]
  );

  if (!chart) {
    return (
      <div
        className={`app-chart-empty ${className}`.trim()}
        style={{ height }}
        role="img"
        aria-label="Risk score history chart — not enough data"
      >
        Not enough history yet (need at least 2 snapshots)
      </div>
    );
  }

  return (
    <div className={`app-risk-score-chart ${className}`.trim()}>
      <div
        className="app-dual-chart__svg-wrap"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label="Risk score history from daily snapshots, y-axis 0 to 100"
      >
        <svg viewBox={`0 0 ${chart.w} ${chart.h}`} width="100%" height={chart.h} preserveAspectRatio="none">
          {chart.gridLines.map((g) => (
            <g key={g.value}>
              <line
                x1={chart.pad.left}
                x2={chart.w - chart.pad.right}
                y1={g.y}
                y2={g.y}
                stroke="var(--border-subtle)"
                strokeWidth={1}
              />
              <text
                x={chart.pad.left - 6}
                y={g.y + 3}
                textAnchor="end"
                fontSize={9}
                fill="var(--text-subtle)"
              >
                {g.value}
              </text>
            </g>
          ))}

          <path
            d={chart.linePath}
            fill="none"
            stroke="var(--risk-high)"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {chart.pts.map((p) => (
            <circle
              key={`${p.i}-${p.recordedAt || p.score}`}
              cx={p.x}
              cy={p.y}
              r={hover?.i === p.i ? 5 : 3.5}
              fill="var(--risk-high)"
              stroke="#fff"
              strokeWidth={hover?.i === p.i ? 2 : 1.5}
            />
          ))}

          {chart.pts.map((p, i) => {
            if (i % Math.ceil(chart.pts.length / 6) !== 0 && i !== chart.pts.length - 1) return null;
            const lbl =
              p.label ||
              (p.recordedAt
                ? new Date(p.recordedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : '');
            if (!lbl) return null;
            return (
              <text
                key={`lbl-${i}`}
                x={p.x}
                y={chart.h - 6}
                textAnchor="middle"
                fontSize={9}
                fill="var(--text-subtle)"
              >
                {lbl}
              </text>
            );
          })}

          {hover && (
            <line
              x1={hover.x}
              x2={hover.x}
              y1={chart.pad.top}
              y2={chart.h - chart.pad.bottom}
              stroke="var(--text-subtle)"
              strokeWidth={1}
              strokeDasharray="4 4"
              opacity={0.5}
            />
          )}
        </svg>

        {hover && (
          <div
            className="app-dual-chart__tooltip"
            style={{
              left: `${Math.max(8, Math.min(92, (hover.x / chart.w) * 100))}%`,
              top: 8,
            }}
          >
            {(hover.label || hover.recordedAt) && (
              <div className="app-dual-chart__tooltip-date">
                {hover.label ||
                  new Date(hover.recordedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
              </div>
            )}
            <div>
              Risk score: <strong>{Math.round(hover.score)}</strong>
              <span style={{ color: 'var(--text-subtle)' }}> / {RISK_SCORE_CHART_MAX}</span>
            </div>
          </div>
        )}
      </div>

      {chart.current != null &&
        chart.latest &&
        Math.round(chart.current) !== Math.round(chart.latest.score) && (
          <p className="text-[11px] mt-2" style={{ color: 'var(--text-subtle)' }}>
            Current score {Math.round(chart.current)} — latest snapshot {Math.round(chart.latest.score)}
          </p>
        )}
    </div>
  );
}
